import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { OtherContact } from "types/OtherContact";

interface OtherContactsContextType {
  otherContacts: OtherContact[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  otherContactsCount: number;
  markContactAsRead: (contactId: number) => void;
  updateLastReadAt: (contactId: number, lastReadAt: string) => void;
}

const OtherContactsContext = createContext<OtherContactsContextType | undefined>(
  undefined
);

export function OtherContactsProvider({ children }: { children: ReactNode }) {
  const [otherContacts, setOtherContacts] = useState<OtherContact[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOtherContacts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCredentials("/manager-api/other-contacts/");
      const transformed = data.map((row: any) => ({
        id: row.id,
        contact: {
          id: row.id,
          name: row.name,
          phone: row.phone,
          landlinePhone: row.landlinePhone,
          mail: row.mail,
          wappPhoneInvalid: row.wappPhoneInvalid,
          lastSeen: "",
        },
        lastMessageAt: row.lastMessageAt ?? null,
        hasUnread: Boolean(row.hasUnread),
        lastReadAt: row.lastReadAt ?? null,
        replyMute: row.replyMute ?? null,
      }));
      setOtherContacts(transformed);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching other contacts");
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchOtherContacts();
  }, [fetchOtherContacts]);

  const markContactAsRead = useCallback((contactId: number) => {
    setOtherContacts((prev) =>
      prev.map((row) =>
        row.contact.id === contactId ? { ...row, hasUnread: false } : row
      )
    );
  }, []);

  const updateLastReadAt = useCallback((contactId: number, lastReadAt: string) => {
    setOtherContacts((prev) =>
      prev.map((row) =>
        row.contact.id === contactId ? { ...row, lastReadAt } : row
      )
    );
  }, []);

  const otherContactsCount = useMemo(
    () => otherContacts.length,
    [otherContacts]
  );

  return (
    <OtherContactsContext.Provider
      value={{
        otherContacts,
        loading,
        error,
        refresh,
        otherContactsCount,
        markContactAsRead,
        updateLastReadAt,
      }}
    >
      {children}
    </OtherContactsContext.Provider>
  );
}

export function useOtherContacts() {
  const context = useContext(OtherContactsContext);
  if (!context) {
    throw new Error(
      "useOtherContacts must be used within an OtherContactsProvider"
    );
  }
  return context;
}
